import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Lottie from 'lottie-react';
import {
  Button,
  Col,
  ModalHeader,
  ModalBody,
  Row,
  ModalFooter,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import trialFile from '../../../../../public/images/lottie/14-day-trial.json';

import { fetchUserProfile } from '../../actions/Auth';

import { APIstartTrial } from '../../api/Payments';
import { APIupdateSettings } from '../../api/Settings';

import { getSetting } from '../../helpers/Settings';

import ErrorBoundary from '../ErrorBoundary';
import ProcessingSpinner from '../ProcessingSpinner';

const StartTrialModal = ({
  handleClose,
  messageOnly,
  fetchProfile,
}) => {
  const intl = useIntl();

  const [trialStarted, setTrialStarted] = useState(false);
  const [loading, setLoading] = useState(false);

  const lottieRef = useRef(null);

  const handleTrial = () => {
    setLoading(true);
    if ((!trialStarted || getSetting('restart_trial_welcome_message'))) {
      APIstartTrial().then(() => {
        setTrialStarted(true);
        if (lottieRef.current) {
          lottieRef.current.play();
        }
        fetchProfile();
      }).catch(() => {
        handleClose();
      }).finally(() => {
        setLoading(false);
      });
    } else if (messageOnly && getSetting('show_trial_welcome_message')) {
      APIupdateSettings({ show_trial_welcome_message: false }).then(() => {
        fetchProfile().then(() => {
          setLoading(false);
          handleClose();
        });
      });
    } else if (trialStarted) {
      window.location.reload();
    }
  };

  useEffect(() => {
    if (messageOnly) {
      setTrialStarted(true);
      if (lottieRef.current) {
        lottieRef.current.play();
      }
    }
  }, [messageOnly]);

  return (
    <ErrorBoundary>
      <div className="modal-start-trial-wrapper">
        <div className="modal-start-trial-background-wrapper">
          <Lottie
            animationData={trialFile}
            loop={false}
            autoplay={false}
            lottieRef={lottieRef}
          />
        </div>
        <div className="modal-start-trial-container">
          <ModalHeader toggle={(!trialStarted || getSetting('restart_trial_welcome_message')) ? handleClose : null} />
          <ModalBody>
            <Row>
              <Col md={6}>
                <div className="modal-start-trial-content">
                  {getSetting('restart_trial_welcome_message') ? (
                    <div>
                      <h3>Welcome back 👋</h3>
                      <p>
                        We’ve added a ton of new features to help you
                        get organized faster. Get instant access to all
                        our Premium features for another 14-days—on us!
                      </p>
                      <ul className="fa-ul">
                        <li>
                          <span className="fa-li">
                            <i className="fa-regular fa-arrow-right" />
                          </span>
                          Facial Recognition
                        </li>
                        <li>
                          <span className="fa-li">
                            <i className="fa-regular fa-arrow-right" />
                          </span>
                          Content Portals
                        </li>
                        <li>
                          <span className="fa-li">
                            <i className="fa-regular fa-arrow-right" />
                          </span>
                          File Versioning
                        </li>
                        <li>
                          <span className="fa-li">
                            <i className="fa-regular fa-arrow-right" />
                          </span>
                          <a href="https://stockpress.co/pricing/#features" target="_blank" rel="noreferrer">
                            {intl.formatMessage({ defaultMessage: 'and so much more!' })}
                          </a>
                        </li>
                      </ul>
                      <div className="modal-button-wrapper">
                        <Button
                          onClick={handleTrial}
                        >
                          <ProcessingSpinner show={loading} />
                          Restart your 14-day trial
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <h3>
                        {(messageOnly && getSetting('show_trial_welcome_message')) ? (
                          intl.formatMessage({ defaultMessage: 'You\'ve unlocked all Premium features, free for 14 days!' })
                        ) : (
                          <>
                            {trialStarted ?
                              intl.formatMessage({ defaultMessage: 'You\'re in!' }) :
                              intl.formatMessage({ defaultMessage: 'Unlock all Premium features, free for 14 days!' })}
                          </>
                        )}
                      </h3>
                      <p>
                        {(messageOnly && getSetting('show_trial_welcome_message')) ? (
                          intl.formatMessage({
                            defaultMessage: 'Try everything that Stockpress has to offer before picking the plan that\'s right for you. At the end of your trial, you\'ll move automatically to our Free plan unless you upgrade.',
                          })
                        ) : (
                          <>
                            {trialStarted ? (
                              intl.formatMessage({
                                defaultMessage: 'You now have access to all Stockpress Premium features for 14 days. At the end of your trial, you\'ll move automatically to our Free plan unless you upgrade.',
                              })
                            ) : (
                              intl.formatMessage({
                                defaultMessage: 'Try everything that Stockpress has to offer before picking the plan that\'s right for you. At the end of your trial, you\'ll move automatically to our Free plan unless you upgrade.',
                              })
                            )}
                          </>
                        )}
                      </p>
                      {(messageOnly && getSetting('show_trial_welcome_message')) ? (
                        <ul className="fa-ul">
                          <li>
                            <span className="fa-li">
                              <i className="fa-regular fa-arrow-right" />
                            </span>
                            {intl.formatMessage({
                              defaultMessage: 'Try Guest Uploads',
                            })}
                          </li>
                          <li>
                            <span className="fa-li">
                              <i className="fa-regular fa-arrow-right" />
                            </span>
                            {intl.formatMessage({
                              defaultMessage: 'Create Content Portals',
                            })}
                          </li>
                          <li>
                            <span className="fa-li">
                              <i className="fa-regular fa-arrow-right" />
                            </span>
                            {intl.formatMessage({
                              defaultMessage: 'Identify duplicate files',
                            })}
                          </li>
                          <li>
                            <span className="fa-li">
                              <i className="fa-regular fa-arrow-right" />
                            </span>
                            <a href="https://stockpress.co/pricing/#features" target="_blank" rel="noreferrer">
                              {intl.formatMessage({ defaultMessage: 'and so much more!' })}
                            </a>
                          </li>
                        </ul>
                      ) : (
                        <>
                          {!trialStarted && (
                            <ul className="fa-ul">
                              <li>
                                <span className="fa-li">
                                  <i className="fa-regular fa-arrow-right" />
                                </span>
                                {intl.formatMessage({
                                  defaultMessage: 'No credit card required!',
                                })}
                              </li>
                              <li>
                                <span className="fa-li">
                                  <i className="fa-regular fa-arrow-right" />
                                </span>
                                {intl.formatMessage({
                                  defaultMessage: 'Access to ALL Premium features',
                                })}
                              </li>
                              <li>
                                <span className="fa-li">
                                  <i className="fa-regular fa-arrow-right" />
                                </span>
                                {intl.formatMessage({
                                  defaultMessage: 'Unlimited users',
                                })}
                              </li>
                              <li>
                                <span className="fa-li">
                                  <i className="fa-regular fa-arrow-right" />
                                </span>
                                <a href="https://stockpress.co/pricing/#features" target="_blank" rel="noreferrer">
                                  {intl.formatMessage({ defaultMessage: 'and so much more!' })}
                                </a>
                              </li>
                            </ul>
                          )}
                        </>
                      )}
                      <div className="modal-button-wrapper">
                        <Button
                          onClick={handleTrial}
                        >
                          <ProcessingSpinner show={loading} />
                          {trialStarted ?
                            intl.formatMessage({ defaultMessage: 'Get started' }) :
                            intl.formatMessage({ defaultMessage: 'Unlock Premium for 14 days' })}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </Col>
              <Col md={6} />
            </Row>
          </ModalBody>
          <ModalFooter />
        </div>
      </div>
    </ErrorBoundary>
  );
};

const mapDispatchToProps = dispatch => ({
  fetchProfile: () => (
    dispatch(fetchUserProfile())
  ),
});

StartTrialModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  fetchProfile: PropTypes.func.isRequired,
  messageOnly: PropTypes.bool,
};

StartTrialModal.defaultProps = {
  messageOnly: false,
};

export default connect(null, mapDispatchToProps)(StartTrialModal);
